import { FC } from 'react'
import { match } from 'ts-pattern'

import {
  Badge,
  BadgeSentiment,
  Box,
  BorderRadius6,
  Flex,
  GreyGrey30,
  GreyGrey80,
  GreyWhite,
  Link,
  Primary13SemiBold,
  ShadowM,
  ShadowXs,
  Skeleton,
  Text
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'

import { automationsPaths } from 'Automations/router'
import {
  presentationToNodeTypes,
  useResolvedNodeDefinitions
} from 'Automations/nodeDefinitions'
import { UNTITLED_AUTOMATION_NAME } from 'Automations/automationEditor/constants'

export const WorkflowLink: FC<{
  automationId: string
  automationVersionId: string
}> = ({ automationId, automationVersionId }) => {
  const definitions = useResolvedNodeDefinitions()

  const automationQuery = useQuery(
    dashboardv99990101Client.getAutomation,
    { id: automationId },
    {
      staleTime: 1 * MINUTES,
      select: data => {
        return {
          name: data?.name,
          archivedAt: data?.archivedAt
        }
      }
    }
  )

  const automationVersionQuery = useQuery(
    dashboardv99990101Client.getAutomationVersion,
    { automationId, versionId: automationVersionId }
  )

  const nodeType = presentationToNodeTypes(
    automationVersionQuery.data?.presentation,
    definitions,
    {
      filterNodeTypes: ['END', 'NOTE'],
      filterStartNodes: true
    }
  ).at(0)

  const Icon = nodeType ? definitions[nodeType].icon : null

  const isArchived = Boolean(automationQuery.data?.archivedAt)
  const isActiveVersion = Boolean(automationVersionQuery.data?.active)
  const status = match({ isArchived, isActiveVersion })
    .returnType<WorkflowStatus>()
    .with({ isArchived: true }, () => 'ARCHIVED')
    .with({ isActiveVersion: true }, () => 'PUBLISHED')
    .otherwise(() => 'UNPUBLISHED')
  const isStatusPending =
    automationQuery.isPending || automationVersionQuery.isPending

  return (
    <Flex
      {...(isArchived
        ? {}
        : {
            as: Link.Unstyled,
            to: automationsPaths.automationsEditor({ automationId }),
            target: '_blank',
            rel: 'noopener noreferrer',
            _hover: { boxShadow: ShadowM }
          })}
      data-testid="workflows.inspector.content.workflowLink"
      alignItems="center"
      gap="8px"
      width="100%"
      padding="6px"
      backgroundColor={GreyWhite}
      border={`${GreyGrey30} 1px solid`}
      borderRadius={BorderRadius6}
      boxShadow={ShadowXs}
      transition="box-shadow ease-in-out 250ms"
    >
      {automationVersionQuery.isPending ? (
        <Skeleton height="24px" width="24px" />
      ) : (
        Icon && (
          <Box
            borderRadius={BorderRadius6}
            overflow="hidden"
            data-testid="workflows.inspector.content.workflowIcon"
          >
            <Icon boxSize="24px" />
          </Box>
        )
      )}

      <Text
        {...Primary13SemiBold}
        color={GreyGrey80}
        flex="1"
        minWidth={0}
        noOfLines={1}
      >
        {automationQuery.isPending || !automationQuery.data ? (
          <Skeleton width="50%" />
        ) : (
          automationQuery.data.name || UNTITLED_AUTOMATION_NAME
        )}
      </Text>

      {isStatusPending ? (
        <Skeleton height="24px" width="65px" />
      ) : (
        <StatusBadge status={status} />
      )}
    </Flex>
  )
}

type WorkflowStatus = 'PUBLISHED' | 'UNPUBLISHED' | 'ARCHIVED'

const STATUS_BADGES: Record<
  WorkflowStatus,
  { label: string; sentiment: BadgeSentiment }
> = {
  PUBLISHED: { label: 'Published', sentiment: 'success' },
  UNPUBLISHED: { label: 'Unpublished', sentiment: 'neutral' },
  ARCHIVED: { label: 'Archived', sentiment: 'neutral' }
}

const StatusBadge: FC<{ status: WorkflowStatus }> = ({ status }) => (
  <Badge
    data-testid="workflows.inspector.content.workflowStatus"
    sentiment={STATUS_BADGES[status].sentiment}
  >
    {STATUS_BADGES[status].label}
  </Badge>
)
